import { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { formatTimestamp } from '../utils/dateUtils';
import './DataModal.css';

function DataModal({ data, onClose }) {
  const [modalContent, setModalContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        let response;
        if (data.type === 'timestamped') {
          response = await apiService.fetchTimestampedData(data.unit, data.id, true);
        } else {
          response = await apiService.fetchPlainData(data.unit, data.id, true);
        }

        if (response.error) {
          setError(response.error);
        } else if (!response.items || response.items.length === 0) {
          setError('No data found');
        } else {
          setModalContent(response.items);
        }
      } catch (err) {
        setError(`Error loading data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [data]);

  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal') {
      onClose();
    }
  };

  const renderTimestampedData = (items) => {
    const item = items[items.length - 1]; // Get latest record

    return (
      <div>
        <div className="data-info">
          <div className="data-row">
            <span className="data-label">Created:</span>
            <span className="data-value">{formatTimestamp(item.created)}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Updated:</span>
            <span className="data-value">{formatTimestamp(item.updated)}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Total Records:</span>
            <span className="data-value">{items.length}</span>
          </div>
        </div>

        {item.deserializedData && Array.isArray(item.deserializedData) ? (
          <>
            <h4 className="candles-title">Candles ({item.deserializedData.length})</h4>
            <table className="candle-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>High</th>
                  <th>Low</th>
                  <th>Avg</th>
                  <th>Entry</th>
                  <th>Samples</th>
                </tr>
              </thead>
              <tbody>
                {item.deserializedData.map((candle, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{candle.High?.toFixed(2) ?? 'N/A'}</td>
                    <td>{candle.Low?.toFixed(2) ?? 'N/A'}</td>
                    <td>{candle.Avg?.toFixed(2) ?? 'N/A'}</td>
                    <td>{candle.Entry?.toFixed(2) ?? 'N/A'}</td>
                    <td>{candle.Samples ?? 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : item.deserializationError ? (
          <div className="error-modal">{item.deserializationError}</div>
        ) : (
          <div className="no-data">No deserialized data available</div>
        )}
      </div>
    );
  };

  const renderPlainData = (items) => {
    return (
      <div>
        {items.map((item, index) => (
          <div key={index} className="plain-data-item">
            <h3>Record {index + 1}</h3>
            <div className="data-row">
              <span className="data-label">Created:</span>
              <span className="data-value">{formatTimestamp(item.created)}</span>
            </div>
            <div className="data-row">
              <span className="data-label">Updated:</span>
              <span className="data-value">{formatTimestamp(item.updated)}</span>
            </div>
            <div className="data-row">
              <span className="data-label">Data Size:</span>
              <span className="data-value">{item.data ? item.data.length : 0} bytes</span>
            </div>
            {item.deserializedData !== null && item.deserializedData !== undefined ? (
              <div className="deserialized-content">
                <h4>Deserialized Content:</h4>
                <pre>{item.deserializedData}</pre>
              </div>
            ) : item.deserializationError ? (
              <div className="error-modal">{item.deserializationError}</div>
            ) : (
              <div className="no-data">No deserialized data available</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modal" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{data.type === 'timestamped' ? 'Deserialized Data' : 'Plain Data'} - ID: {data.id}</h2>
          <span className="close" onClick={onClose}>&times;</span>
        </div>
        <div className="modal-body">
          {loading && <div className="loading-modal">Loading deserialized data...</div>}
          {error && <div className="error-modal">Error: {error}</div>}
          {!loading && !error && modalContent && (
            data.type === 'timestamped'
              ? renderTimestampedData(modalContent)
              : renderPlainData(modalContent)
          )}
        </div>
      </div>
    </div>
  );
}

export default DataModal;

